import { VIDEO_FORMATS } from "../../../utils/constants";
import { getExtensionFile } from "../../../utils/string";
import { isValidBulkContentID } from "../../../utils/bulkCampaignContent";

const YOUTUBE_HOSTS = ["youtube.com", "youtu.be", "youtube-nocookie.com"];

const FIELD_LABEL_KEYS = {
  titles: "bulk.field.titles",
  headlines: "bulk.field.headlines",
  longHeadlines: "bulk.field.longHeadlines",
  descriptions: "bulk.field.descriptions",
  keywords: "bulk.field.keywords",
  keywordsNegative: "bulk.field.keywordsNegative",
  searchThemes: "bulk.field.searchThemes",
  callOuts: "bulk.field.callOuts",
  callToAction: "bulk.field.callToAction",
  primaryText: "bulk.field.primaryText",
  headline: "bulk.field.headline",
  longHeadline: "bulk.field.longHeadline",
  searchTheme: "bulk.field.searchTheme",
  destinationURL: "bulk.field.destinationURL",
  destinationUrl: "bulk.field.destinationURL",
  dailyBudget: "bulk.field.dailyBudget",
  language: "bulk.field.language",
  objective: "bulk.field.objective",
  audience: "bulk.field.audience",
  locations: "bulk.field.locations",
  pageID: "bulk.field.page",
  "references.pageID": "bulk.field.page",
  instagramID: "bulk.field.instagram",
  "references.instagramID": "bulk.field.instagram",
  identityTikTokID: "bulk.field.identity",
  "references.identityTikTokID": "bulk.field.identity",
  appID: "bulk.field.app",
  "references.appID": "bulk.field.app",
  appStore: "bulk.field.appStore",
  "references.appStore": "bulk.field.appStore",
  adFormat: "bulk.field.adFormat",
  "references.adFormat": "bulk.field.adFormat",
  merchantCenterID: "bulk.field.merchantCenter",
  "references.merchantCenterID": "bulk.field.merchantCenter",
  adAccountID: "bulk.field.account",
  integrationID: "bulk.field.account",
};

/**
 * RTDB drops nulls, empty arrays and empty objects, and can turn an array with
 * holes into an indexed object. Missing lists are empty.
 */
export const readArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null || typeof value !== "object") return [];

  const keys = Object.keys(value);
  if (keys.length === 0 || !keys.every((key) => /^\d+$/.test(key))) return [];

  return keys
    .sort((left, right) => Number(left) - Number(right))
    .map((key) => value[key])
    .filter((item) => item != null);
};

/**
 * Rebuilds every list RTDB handed back as an indexed object, at any depth.
 */
const restoreArrays = (value) => {
  if (value == null || typeof value !== "object") return value;
  const list = readArray(value);
  if (Array.isArray(value) || list.length > 0) return list.map(restoreArrays);
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, restoreArrays(item)])
  );
};

export const buildBulkReview = (generationData = {}) => ({
  platform: generationData.platform ?? null,
  campaignType: generationData.campaignType ?? null,
  fileName: generationData.fileName ?? "",
  campaigns: restoreArrays(readArray(generationData.campaigns)),
});

const stripCreatives = (creatives) =>
  readArray(creatives)
    .filter(
      (creative) => creative?.slot && isValidBulkContentID(creative?.contentID)
    )
    .map(({ slot, contentID }) => ({ slot, contentID }));

const toCreationNode = (node) => {
  const rest = { ...(node ?? {}) };
  delete rest.creativeSlots;
  return { ...rest, creatives: stripCreatives(rest.creatives) };
};

// Only the picked creatives go back, as { slot, contentID }. The backend
// validates the rest and gives each ad a unique name.
export const toCreationCampaigns = (campaigns) =>
  readArray(campaigns).map((campaign) => ({
    ...campaign,
    adGroups: readArray(campaign?.adGroups).map((adGroup) => ({
      ...toCreationNode(adGroup),
      ads: readArray(adGroup?.ads).map(toCreationNode),
    })),
  }));

const contentUrl = (item) => item?.url || readArray(item?.urls)[0] || "";

const isYoutubeUrl = (url) => {
  try {
    const host = new URL(String(url)).hostname.replace(/^www\./, "");
    return YOUTUBE_HOSTS.some(
      (item) => host === item || host.endsWith(`.${item}`)
    );
  } catch (_error) {
    return false;
  }
};

const mediaFormat = (item) =>
  String(item?.format ?? "")
    .toLowerCase()
    .replace(/^video\//, "");

const derivePoolContentType = (item) => {
  if (item?.isCarousel || item?.type === "carousel") return "carousel";
  if (isYoutubeUrl(contentUrl(item))) return "video";
  if (item?.type === "image" || item?.type === "video") return item.type;
  if (VIDEO_FORMATS.includes(mediaFormat(item))) return "video";

  const extension = getExtensionFile(contentUrl(item));
  if (VIDEO_FORMATS.includes(extension)) return "video";
  if (contentUrl(item) || item?.type === "upload") return "image";
  return null;
};

export const toPoolContent = (item) => {
  const contentID = item?.id || item?.contentID || null;
  if (!isValidBulkContentID(contentID)) return null;

  const type = derivePoolContentType(item);
  if (!type) return null;

  return {
    contentID: String(contentID),
    url: contentUrl(item),
    type,
    aspectRatio: item?.aspectRatio ?? item?.analysis?.aspectRatio ?? null,
    width: item?.width ?? item?.analysis?.width ?? null,
    height: item?.height ?? item?.analysis?.height ?? null,
    name: item?.name || item?.fileName || "",
  };
};

export const toPoolContentFromSelection = ({ selection, record }) => {
  const recordType = record?.type === "upload" ? null : record?.type;
  const selectionType = selection?.type === "upload" ? null : selection?.type;

  return toPoolContent({
    ...(record ?? {}),
    id:
      selection?.id || selection?.contentID || record?.id || record?.contentID,
    url: selection?.url || record?.url,
    isCarousel: Boolean(selection?.isCarousel || record?.isCarousel),
    type: recordType || selectionType,
    width: record?.width ?? record?.analysis?.width ?? selection?.width,
    height: record?.height ?? record?.analysis?.height ?? selection?.height,
    aspectRatio:
      record?.aspectRatio ??
      record?.analysis?.aspectRatio ??
      selection?.aspectRatio,
    name: record?.name || record?.fileName || selection?.name || "",
  });
};

const MEASURE_TIMEOUT_MS = 8000;

const withMeasuredRatio = ({ content, width, height }) => {
  if (!(width > 0) || !(height > 0)) return content;
  return {
    ...content,
    width,
    height,
    aspectRatio: content.aspectRatio ?? width / height,
  };
};

export const measurePoolContentSize = (content) =>
  new Promise((resolve) => {
    const width = Number(content?.width);
    const height = Number(content?.height);
    if (width > 0 && height > 0) {
      resolve(withMeasuredRatio({ content, width, height }));
      return;
    }

    const url = content?.url;
    if (!url || typeof document === "undefined") {
      resolve(content);
      return;
    }

    let settled = false;
    const finish = (next) => {
      if (settled) return;
      settled = true;
      resolve(next);
    };
    const timer = setTimeout(() => finish(content), MEASURE_TIMEOUT_MS);

    if (content?.type === "video") {
      const video = document.createElement("video");
      video.preload = "metadata";
      const done = (next) => {
        clearTimeout(timer);
        video.removeAttribute("src");
        video.load();
        finish(next);
      };
      video.onloadedmetadata = () => {
        done(
          withMeasuredRatio({
            content,
            width: video.videoWidth,
            height: video.videoHeight,
          })
        );
      };
      video.onerror = () => done(content);
      video.src = url;
      return;
    }

    const image = new Image();
    image.onload = () => {
      clearTimeout(timer);
      finish(
        withMeasuredRatio({
          content,
          width: image.naturalWidth,
          height: image.naturalHeight,
        })
      );
    };
    image.onerror = () => {
      clearTimeout(timer);
      finish(content);
    };
    image.src = url;
  });

const overlayPoolContent = ({ base, extra }) => ({
  ...base,
  url: base.url || extra.url,
  type: base.type || extra.type,
  width: base.width ?? extra.width,
  height: base.height ?? extra.height,
  aspectRatio: base.aspectRatio ?? extra.aspectRatio,
  name: base.name || extra.name,
});

export const mergePoolContents = ({
  current = [],
  incoming = [],
  append = false,
} = {}) => {
  if (!append) {
    if (current.length === 0) return incoming;
    const byId = new Map(incoming.map((item) => [item.contentID, item]));
    return current.map((item) => {
      const extra = byId.get(item.contentID);
      if (!extra) return item;
      return overlayPoolContent({ base: item, extra });
    });
  }

  const merged = new Map(current.map((item) => [item.contentID, item]));
  incoming.forEach((item) => {
    if (!item?.contentID) return;
    const previous = merged.get(item.contentID);
    if (!previous) {
      merged.set(item.contentID, item);
      return;
    }
    merged.set(
      item.contentID,
      overlayPoolContent({ base: previous, extra: item })
    );
  });
  return [...merged.values()];
};

export const sameContentIDs = ({ left = [], right = [] } = {}) => {
  if (left.length !== right.length) return false;
  const rightIDs = new Set(right.map((item) => item.contentID));
  return left.every((item) => rightIDs.has(item.contentID));
};

export const translateBulkToken = ({ t, token, prefix }) => {
  if (token == null || token === "") return "";
  const mapped = prefix === "bulk.field" ? FIELD_LABEL_KEYS[token] : null;
  const key = mapped || `${prefix}.${token}`;
  const translated = t(key);
  return translated === key ? String(token) : translated;
};
